import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { apiGet } from '@/lib/api';
import { useDebounce } from '@/hooks/use-debounce';
import type { SearchGroup } from '@/lib/types';
import { cn } from '@/lib/utils';

type CommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Global search (spec §5.11). Results are grouped by type and keyboard
 * navigable; the API only returns what this user is allowed to see.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const debounced = useDebounce(term, 250);

  const { data: groups = [], isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => apiGet<SearchGroup[]>('/search', { params: { q: debounced } }),
    enabled: open && debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  // Flattened so arrow keys move across group boundaries.
  const flatHits = groups.flatMap((group) => group.hits);

  useEffect(() => {
    setActiveIndex(0);
  }, [debounced]);

  useEffect(() => {
    if (!open) setTerm('');
  }, [open]);

  // Ctrl/Cmd + K anywhere in the app.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onOpenChange(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onOpenChange]);

  const go = (link: string) => {
    onOpenChange(false);
    navigate(link);
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, flatHits.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const hit = flatHits[activeIndex];
      if (hit) go(hit.link);
    }
  };

  let runningIndex = -1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[15%] max-w-xl translate-y-0 gap-0 p-0">
        <DialogTitle className="sr-only">Search</DialogTitle>

        <div className="flex items-center gap-2 border-b px-4">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            autoFocus
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Search people, teams, documents…"
            className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>

        <div className="scrollbar-thin max-h-[22rem] overflow-y-auto p-2">
          {term.trim().length < 2 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              Type at least two characters to search.
            </p>
          ) : isFetching && groups.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">Searching…</p>
          ) : groups.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              Nothing matched &ldquo;{term}&rdquo;.
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.type} className="mb-2">
                <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </div>
                {group.hits.map((hit) => {
                  runningIndex += 1;
                  const isActive = runningIndex === activeIndex;
                  return (
                    <button
                      key={`${hit.type}-${hit.id}`}
                      onClick={() => go(hit.link)}
                      onMouseEnter={() => setActiveIndex(flatHits.indexOf(hit))}
                      className={cn(
                        'flex w-full flex-col items-start rounded-md px-2 py-2 text-left text-sm transition-colors',
                        isActive ? 'bg-accent' : 'hover:bg-accent/60',
                      )}
                    >
                      <span className="truncate font-medium">{hit.title}</span>
                      {hit.subtitle && (
                        <span className="truncate text-xs text-muted-foreground">{hit.subtitle}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
